import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';

export const MAX_IMAGES_PER_POST = 9;
export const SOCIAL_MEDIA_TYPE_IMAGE = 'IMAGE';
export const SUPPORTED_UPLOAD_MIME_TYPES = Object.freeze(['image/jpeg', 'image/png', 'image/gif']);
export const SUPPORTED_UPLOAD_EXTENSIONS = Object.freeze(['.jpg', '.jpeg', '.png', '.gif']);

let localSequence = 0;

export function nextMediaLocalId() {
  localSequence += 1;
  return `media-${Date.now()}-${localSequence}`;
}

export function isSupportedUploadFile(file) {
  const type = String(file?.type || '').toLowerCase();
  if (SUPPORTED_UPLOAD_MIME_TYPES.includes(type)) return true;
  const name = String(file?.name || '').toLowerCase();
  return SUPPORTED_UPLOAD_EXTENSIONS.some((suffix) => name.endsWith(suffix));
}

export function normalizeMediaDisplayOrder(items) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    ...item,
    displayOrder: index,
  }));
}

export function mediaFingerprint(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => `${item.displayOrder}:${item.fileReference || ''}`)
    .join('|');
}

export function buildMediaWritePayload(items) {
  return normalizeMediaDisplayOrder(items)
    .filter((item) => item.uploadState == null || item.uploadState === 'ready')
    .filter((item) => String(item.fileReference || '').trim())
    .map((item) => ({
      mediaType: SOCIAL_MEDIA_TYPE_IMAGE,
      fileReference: String(item.fileReference).trim(),
      displayOrder: item.displayOrder,
    }));
}

export function hasPendingMediaUpload(items) {
  return (Array.isArray(items) ? items : []).some((item) => (
    item.uploadState === 'uploading' || item.uploadState === 'failed'
  ));
}

export function composerItemsFromReadMedia(media) {
  return normalizeMediaDisplayOrder((Array.isArray(media) ? media : []).map((item) => ({
    localId: nextMediaLocalId(),
    persistedId: item.id == null ? null : String(item.id),
    fileReference: item.fileReference,
    previewUrl: item.url || '',
    uploadState: 'ready',
    displayOrder: item.displayOrder,
  })));
}

/**
 * Read rule: missing/null media is legacy and may fall back to cover.
 * An explicit empty array is authoritative and must not fall back.
 */
export function resolveReadMedia(raw, resolveUrl = resolveMemberMediaUrl) {
  if (raw == null || raw.media == null) {
    const cover = String(raw?.coverFileReference || '').trim();
    if (!cover) return [];
    return [{
      id: null,
      mediaType: SOCIAL_MEDIA_TYPE_IMAGE,
      fileReference: cover,
      displayOrder: 0,
      url: resolveUrl(cover),
      synthetic: true,
    }];
  }
  if (!Array.isArray(raw.media) || raw.media.length === 0) return [];
  return raw.media
    .map((item, index) => {
      const fileReference = String(item?.fileReference || '').trim();
      if (!fileReference) return null;
      const order = Number.isInteger(item?.displayOrder) ? item.displayOrder : index;
      return {
        id: item?.id == null || item.id === '' ? null : String(item.id),
        mediaType: SOCIAL_MEDIA_TYPE_IMAGE,
        fileReference,
        displayOrder: order,
        url: resolveUrl(fileReference),
        synthetic: item?.id == null,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.displayOrder - right.displayOrder)
    .map((item, index) => ({ ...item, displayOrder: index }));
}

export function selectionExceedsMax(existingCount, incomingCount, max = MAX_IMAGES_PER_POST) {
  return existingCount + incomingCount > max;
}

export function moveMediaItem(items, localId, direction) {
  const list = [...(Array.isArray(items) ? items : [])];
  const index = list.findIndex((item) => item.localId === localId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= list.length) return normalizeMediaDisplayOrder(list);
  const [item] = list.splice(index, 1);
  list.splice(nextIndex, 0, item);
  return normalizeMediaDisplayOrder(list);
}

export function removeMediaItem(items, localId) {
  return normalizeMediaDisplayOrder((Array.isArray(items) ? items : []).filter((item) => item.localId !== localId));
}

export function replaceMediaItemAt(items, localId, next) {
  return normalizeMediaDisplayOrder((Array.isArray(items) ? items : []).map((item) => (
    item.localId === localId ? { ...item, ...next, localId, displayOrder: item.displayOrder } : item
  )));
}
